import React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { btnPrimary } from "../lib/ui";
import {
  FileText, ShieldCheck, Clock, Download, Printer, CheckCircle2, ArrowRight,
} from "lucide-react";

const SampleRow = ({ label, cur, ytd, bold }) => (
  <div className={`grid grid-cols-12 gap-1 py-[2px] text-[9px] ${bold ? "font-bold" : ""}`}>
    <div className="col-span-6 text-[#0f172a]">{label}</div>
    <div className="col-span-3 text-right font-mono-num text-[#0f172a]">{cur}</div>
    <div className="col-span-3 text-right font-mono-num text-[#0f172a]">{ytd}</div>
  </div>
);

const HeroStub = () => (
  <div className="relative">
    <div className="absolute -inset-6 bg-gradient-to-tr from-[#D0E2FF]/50 to-transparent blur-2xl rounded-[2rem]" />
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="relative bg-white border border-[#D0E2FF] shadow-2xl ring-1 ring-black/5 rounded-lg w-full max-w-md overflow-hidden"
    >
      {/* Diagonal SAMPLE watermark (text stays sharp) */}
      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden">
        <span className="font-display font-extrabold tracking-widest text-[#1E40AF]/10 text-6xl" style={{ transform: "rotate(-28deg)" }}>
          SAMPLE
        </span>
      </div>
      <div className="relative p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-bold text-[#0F172A]">JORDAN SAMPLE</div>
            <div className="text-[9px] font-bold text-[#0F172A] mt-1">SAMPLE COMPANY LLC</div>
            <div className="text-[8px] text-[#475569]">123 Sample Street, Atlanta, GA 30303</div>
            <div className="text-[8px] text-[#475569]">Weekly &middot; 40 hrs @ $25.70</div>
          </div>
          <div className="font-display font-bold text-sm text-[#0F172A] whitespace-nowrap">Earnings Statement</div>
        </div>
        <div className="grid grid-cols-12 gap-1 mt-4 text-[9px] font-bold border-b border-slate-800 pb-1">
          <div className="col-span-6">Description</div>
          <div className="col-span-3 text-right">Current</div>
          <div className="col-span-3 text-right">YTD</div>
        </div>
        <SampleRow label="Gross earnings" cur="$1,028.00" ytd="$41,120.00" />
        <SampleRow label="Federal income tax" cur="$96.00" ytd="$3,840.00" />
        <SampleRow label="Social Security" cur="$63.74" ytd="$2,549.60" />
        <SampleRow label="Medicare" cur="$14.91" ytd="$596.40" />
        <SampleRow label="GA state income tax" cur="$45.00" ytd="$1,800.00" />
        <div className="border-t-2 border-slate-800 mt-1 pt-1">
          <SampleRow label="Net pay" cur="$808.35" ytd="$32,334.00" bold />
        </div>
      </div>
      {/* Bottom fade so the card looks cropped */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
    </motion.div>
  </div>
);

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="flex gap-3">
    <div className="shrink-0 w-10 h-10 rounded-lg bg-[#F0F6FF] border border-[#D0E2FF] flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#2563EB]" />
    </div>
    <div>
      <div className="font-semibold text-sm text-[#0F172A]">{title}</div>
      <div className="text-sm text-[#475569]">{desc}</div>
    </div>
  </div>
);

export const Landing = ({ onStart }) => (
  <div className="relative overflow-hidden">
    <div className="absolute inset-0 grain-overlay pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="inline-flex items-center gap-2 rounded-full border border-[#D0E2FF] bg-[#F8FAFC] px-3 py-1 text-xs font-medium text-[#1E40AF] mb-6">
          <ShieldCheck className="w-3.5 h-3.5" /> Built for independent earners
        </div>
        <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#0F172A]">
          Nexus Numbers Paystub Generator
        </h1>
        <p className="mt-4 font-display text-lg sm:text-xl font-semibold text-[#1E40AF]">
          For gig, 1099 workers and the self-employed.
        </p>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-xl">
          Enter your own earnings, hours, pay rate, deductions, and state taxes so your statement reflects your actual pay.
        </p>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#475569] max-w-xl">
          Independent contractors, gig workers, freelancers and small business owners
          rarely get a paystub. Turn the income you actually earned into a clean,
          print-ready earnings statement in minutes — one flat fee, one free revision.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Button
            data-testid="create-paycheck-button"
            onClick={onStart}
            className={`${btnPrimary} rounded-full px-8 py-6 text-base`}
          >
            Create Paystub Now <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <div className="flex items-center gap-2 text-sm text-[#64748B]">
            <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
            $10 flat &middot; 1 free revision
          </div>
        </div>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          <Feature icon={Clock} title="Under 5 minutes" desc="Guided multi-step form." />
          <Feature icon={FileText} title="Standard layout" desc="Earnings, taxes, net pay." />
          <Feature icon={Download} title="Print or download" desc="Instant PDF download." />
        </div>
      </motion.div>

      <div className="flex justify-center lg:justify-end">
        <HeroStub />
      </div>
    </div>

    <div className="border-t border-[#D0E2FF]/60 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8 text-sm text-[#475569]">
        <div className="flex items-center gap-3">
          <Printer className="w-5 h-5 text-[#2563EB]" /> Print-ready, letter-size output
        </div>
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-[#2563EB]" /> Secure PayPal checkout
        </div>
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-[#2563EB]" /> All 50 states supported
        </div>
      </div>
    </div>
  </div>
);
